import {
  FileChartColumnIcon,
  KeyRoundIcon,
  MessageSquareIcon,
  PackageIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  UsersIcon,
} from 'lucide-react';
import * as React from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  LogoMark,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@noor/ui';
import { cn } from '@noor/lib/cn';
import { useCanVisit } from '@/entities/access';
import { isProdHost, isProdNavVisible } from '@/shared/config/cpanel-host';
import {
  FigmaMerchantChartIcon,
  FigmaSidebarLogo,
  RemixBriefcaseLineIcon,
  RemixInboxArchiveLineIcon,
  RemixRestaurant2LineIcon,
  RemixSendPlaneLineIcon,
  RemixShoppingBasketLineIcon,
  RemixShieldFlashLineIcon,
  RemixSidebarFoldIcon,
  RemixSidebarUnfoldIcon,
  RemixStore2FillIcon,
} from './app-sidebar-icons';

export interface AppSidebarProps {
  className?: string;
  /** Force expanded state (useful in tests or debugging) */
  defaultExpanded?: boolean;
  mobile?: boolean;
  onClose?: () => void;
}

const SIDEBAR_STORAGE_KEY = 'cpanel:sidebar-expanded';

type SidebarIcon = React.ComponentType<{ className?: string }>;

interface NavChildLink {
  id: string;
  labelKey: string;
  to: string;
}

interface NavChildDisabled {
  id: string;
  labelKey: string;
  disabled: true;
}

type NavChild = NavChildLink | NavChildDisabled;

interface NavRowBase {
  id: string;
  labelKey: string;
  icon: SidebarIcon;
}

/** Top-level leaf link (the Еда section): navigates directly, no chevron. */
interface NavLinkRow extends NavRowBase {
  to: string;
}

interface NavDisabledRow extends NavRowBase {
  disabled: true;
  /**
   * Disabled group headers (Бухгалтерия) keep the Figma chevron; disabled
   * leaves (Доставка) render without one.
   */
  chevron: boolean;
}

interface NavGroupRow extends NavRowBase {
  children: readonly NavChild[];
  /** Disabled groups render collapsed and inert (e.g. the ОПС section). */
  disabled?: true;
}

type NavRow = NavLinkRow | NavDisabledRow | NavGroupRow;

interface NavSection {
  id: 'platform' | 'delivery' | 'food' | 'administration';
  titleKey: string;
  rows: readonly NavRow[];
}

function isLinkRow(row: NavRow): row is NavLinkRow {
  return 'to' in row;
}

function isGroupRow(row: NavRow): row is NavGroupRow {
  return 'children' in row;
}

/** Shared business accounts are independent of service-specific workflows.
 * Platform users and RBAC live together under administration.
 */
const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'platform',
    titleKey: 'nav.platform',
    rows: [
      {
        id: 'merchants',
        labelKey: 'nav.merchants',
        icon: FigmaMerchantChartIcon,
        to: '/merchants',
      },
      {
        id: 'inbox',
        labelKey: 'nav.inbox',
        icon: MessageSquareIcon,
        to: '/inbox',
      },
      {
        id: 'integration-tokens',
        labelKey: 'nav.tokens',
        icon: KeyRoundIcon,
        to: '/integration-tokens',
      },
    ],
  },
  {
    id: 'delivery',
    titleKey: 'nav.delivery',
    rows: [
      {
        id: 'delivery-accounting',
        labelKey: 'nav.accounting',
        icon: RemixStore2FillIcon,
        disabled: true,
        chevron: true,
      },
      {
        id: 'delivery-orders',
        labelKey: 'nav.orders',
        icon: PackageIcon,
        children: [{ id: 'delivery-orders-all', labelKey: 'nav.orders', to: '/orders' }],
      },
      {
        id: 'delivery-ops',
        labelKey: 'nav.ops',
        icon: RemixInboxArchiveLineIcon,
        children: [
          { id: 'delivery-couriers', labelKey: 'nav.couriers', to: '/couriers' },
          {
            id: 'delivery-courier-offers',
            labelKey: 'nav.courierOffers',
            to: '/couriers/offers',
          },
          { id: 'delivery-map', labelKey: 'nav.map', to: '/tracking' },
          { id: 'delivery-slot', labelKey: 'nav.bonus', to: '/slot' },
        ],
      },
      {
        id: 'delivery-reports',
        labelKey: 'nav.reports',
        icon: FileChartColumnIcon,
        children: [
          {
            id: 'delivery-reports-transactions',
            labelKey: 'nav.transactions',
            to: '/reports/transactions',
          },
          {
            id: 'delivery-reports-self-employed',
            labelKey: 'nav.selfEmployed',
            to: '/reports/self-employed',
          },
        ],
      },
      {
        id: 'delivery-dispatching',
        labelKey: 'nav.dispatching',
        icon: SlidersHorizontalIcon,
        children: [
          {
            id: 'delivery-settings-delivery',
            labelKey: 'nav.deliverySettings',
            to: '/settings/delivery',
          },
          {
            id: 'delivery-settings-eta',
            labelKey: 'nav.etaSettings',
            to: '/settings/eta',
          },
          {
            id: 'delivery-settings-multiorder',
            labelKey: 'nav.multiorderParams',
            to: '/settings/multiorder',
          },
          {
            id: 'delivery-settings-cities',
            labelKey: 'nav.cities',
            to: '/settings/cities',
          },
        ],
      },
    ],
  },
  {
    id: 'food',
    titleKey: 'nav.food',
    rows: [
      {
        id: 'food-delivery',
        labelKey: 'nav.delivery',
        icon: RemixSendPlaneLineIcon,
        disabled: true,
        chevron: false,
      },
      {
        id: 'food-orders',
        labelKey: 'nav.orders',
        icon: RemixShoppingBasketLineIcon,
        to: '/food/orders',
      },
      {
        id: 'food-catalog',
        labelKey: 'nav.catalogManagement',
        icon: RemixRestaurant2LineIcon,
        children: [
          {
            id: 'catalog-restaurants',
            labelKey: 'nav.catalogRestaurants',
            to: '/catalog/restaurants',
          },
          {
            id: 'catalog-categories',
            labelKey: 'nav.categories',
            to: '/catalog/categories',
          },
          {
            id: 'catalog-products',
            labelKey: 'nav.catalogProducts',
            to: '/catalog/products',
          },
          {
            id: 'catalog-promotions',
            labelKey: 'nav.catalogPromotions',
            to: '/catalog/promotions',
          },
        ],
      },
      {
        id: 'food-business',
        labelKey: 'nav.configurations',
        icon: RemixBriefcaseLineIcon,
        children: [
          {
            id: 'business-categories',
            labelKey: 'nav.businessCategories',
            to: '/business/categories',
          },
          {
            id: 'business-cuisines',
            labelKey: 'nav.businessCuisines',
            to: '/business/cuisines',
          },
          {
            id: 'business-banners',
            labelKey: 'nav.banners',
            to: '/business/banners',
          },
          {
            id: 'business-services',
            labelKey: 'nav.settings',
            to: '/settings/services',
          },
        ],
      },
      {
        id: 'food-moderation',
        labelKey: 'nav.moderation',
        icon: RemixShieldFlashLineIcon,
        children: [
          {
            id: 'moderation-restaurants',
            labelKey: 'nav.restaurants',
            to: '/moderation/restaurants',
          },
          {
            id: 'moderation-products',
            labelKey: 'nav.products',
            to: '/moderation/products',
          },
          {
            id: 'moderation-categories',
            labelKey: 'nav.categories',
            to: '/moderation/categories',
          },
          {
            id: 'moderation-promotions',
            labelKey: 'nav.deals',
            to: '/moderation/promotions',
          },
        ],
      },
      {
        id: 'food-storefront',
        labelKey: 'nav.storefront',
        icon: RemixRestaurant2LineIcon,
        children: [
          {
            id: 'storefront-restaurants',
            labelKey: 'nav.restaurants',
            to: '/storefront/restaurants',
          },
          {
            id: 'storefront-products',
            labelKey: 'nav.products',
            to: '/storefront/products',
          },
          {
            id: 'storefront-categories',
            labelKey: 'nav.categories',
            to: '/storefront/categories',
          },
          { id: 'storefront-deals', labelKey: 'nav.deals', to: '/storefront/promotions' },
        ],
      },
    ],
  },
  {
    id: 'administration',
    titleKey: 'nav.administration',
    rows: [
      { id: 'platform-users', labelKey: 'nav.users', icon: UsersIcon, to: '/users' },
      { id: 'platform-access', labelKey: 'nav.rbac', icon: ShieldCheckIcon, to: '/rbac' },
    ],
  },
] as const;

function isChildLink(child: NavChild): child is NavChildLink {
  return 'to' in child;
}

/**
 * Drop links the user may not open (`isVisiblePath`: the routes' permissions,
 * plus production-only navigation hiding). Disabled placeholder rows stay only when
 * `keepPlaceholders` (non-prod hosts). A group survives only while it still
 * holds a link; a section only while it still holds a row.
 */
function visibleSections(
  sections: readonly NavSection[],
  isVisiblePath: (to: string) => boolean,
  keepPlaceholders: boolean,
): readonly NavSection[] {
  const visible: NavSection[] = [];
  for (const section of sections) {
    const rows: NavRow[] = [];
    for (const row of section.rows) {
      if (isLinkRow(row)) {
        if (isVisiblePath(row.to)) rows.push(row);
        continue;
      }
      if (!isGroupRow(row) || row.disabled) {
        if (keepPlaceholders) rows.push(row);
        continue;
      }
      const children = row.children.filter((child) =>
        isChildLink(child) ? isVisiblePath(child.to) : keepPlaceholders,
      );
      if (children.some(isChildLink)) rows.push({ ...row, children });
    }
    if (rows.length > 0) visible.push({ ...section, rows });
  }
  return visible;
}

function collectRowPaths(row: NavRow): string[] {
  if (isLinkRow(row)) {
    return [row.to];
  }
  if (!isGroupRow(row)) {
    return [];
  }
  return row.children.filter(isChildLink).map((child) => child.to);
}

/**
 * Sidebar for Noor CPanel:
 * - Expanded (240px): dark Figma navigation described above.
 * - Desktop starts as a 64px icon rail unless this tab saved an expanded state.
 * - Group icons in the rail open their child links in a popover.
 * - Mobile uses the full 240px panel as a drawer.
 * - No profile avatar per requirements.
 */
export function AppSidebar({
  className,
  defaultExpanded,
  mobile = false,
  onClose,
}: AppSidebarProps) {
  const { t } = useTranslation('common');
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const [isExpanded, setIsExpanded] = React.useState(() => {
    if (mobile) return true;
    if (defaultExpanded !== undefined) return defaultExpanded;
    try {
      return sessionStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [openRailGroup, setOpenRailGroup] = React.useState<string | null>(null);
  const setExpanded = (expanded: boolean) => {
    setIsExpanded(expanded);
    if (mobile) return;
    try {
      sessionStorage.setItem(SIDEBAR_STORAGE_KEY, String(expanded));
    } catch {
      // The sidebar still works when storage is unavailable.
    }
  };
  // Group open-state is derived, not synced: every interactive group starts
  // closed, the group holding the current route reads open, and manual
  // toggles override the derived value per group.
  const [toggledGroups, setToggledGroups] = React.useState<Record<string, boolean>>({});

  const isPathActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  const holdsActiveRoute = (row: NavGroupRow) =>
    row.children.filter(isChildLink).some((child) => isPathActive(child.to));

  const isGroupOpen = (row: NavGroupRow) =>
    toggledGroups[row.id] ?? holdsActiveRoute(row);

  const toggleGroup = (row: NavGroupRow) => {
    const next = !isGroupOpen(row);
    setToggledGroups((prev) => ({ ...prev, [row.id]: next }));
  };

  const isRowActive = (row: NavRow) => collectRowPaths(row).some(isPathActive);

  // Nav items carry no permission of their own: a link shows when the route
  // it points to (and that route's ancestors) is permitted.
  const canVisit = useCanVisit();

  const sections = React.useMemo(
    () =>
      visibleSections(
        NAV_SECTIONS,
        (to) => (!isProdHost() || isProdNavVisible(to)) && canVisit(to),
        !isProdHost(),
      ).map((section) => ({
        ...section,
        // Keep leaves above groups in both the expanded panel and icon rail.
        rows: section.rows
          .map((row): NavRow => {
            if (!isGroupRow(row) || row.disabled || row.children.length !== 1) return row;
            const child = row.children[0];
            return child && isChildLink(child)
              ? { id: row.id, labelKey: child.labelKey, icon: row.icon, to: child.to }
              : row;
          })
          .sort(
            (a, b) =>
              Number(isGroupRow(a) || ('chevron' in a && a.chevron)) -
              Number(isGroupRow(b) || ('chevron' in b && b.chevron)),
          ),
      })),
    [canVisit],
  );

  const flatRows = React.useMemo(
    () => sections.flatMap((section) => section.rows),
    [sections],
  );

  return (
    <aside
      data-slot="app-sidebar"
      data-expanded={isExpanded}
      className={cn(
        'z-30 flex h-full shrink-0 flex-col overflow-hidden border-r border-[#374151] bg-[#111827] select-none',
        'transition-[width] duration-300 ease-in-out motion-reduce:transition-none',
        isExpanded ? 'w-[240px]' : 'w-16',
        mobile && 'fixed inset-y-0 left-0 z-50 w-[240px]',
        className,
      )}
      onClickCapture={(event) => {
        if (mobile && (event.target as HTMLElement).closest('a')) onClose?.();
      }}
    >
      {isExpanded ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* 56px brand header with the collapse glyph */}
          <div className="relative flex h-14 shrink-0 items-center border-b border-[#374151]">
            <Link
              to="/merchants"
              aria-label="Noor Home"
              className="absolute top-1/2 left-5 -translate-y-1/2 rounded outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              {/* The 77x20 Figma logo box crops the 28.23px-tall wordmark art
                  so the letterforms sit centered and the ® rides above. */}
              <span className="relative block h-5 w-[77px]">
                <FigmaSidebarLogo className="absolute top-[-8.22px] left-0 h-[28.23px] w-[77px]" />
              </span>
            </Link>
            <button
              type="button"
              aria-label={mobile ? 'Закрыть меню' : t('nav.collapseSidebar')}
              onClick={() => (mobile ? onClose?.() : setExpanded(false))}
              className="absolute top-1/2 right-[3px] flex size-8 -translate-y-1/2 items-center justify-center rounded-[5px] text-[#d6dff0] transition-colors outline-none hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <RemixSidebarFoldIcon className="size-4" />
            </button>
          </div>

          {/* Navigation content */}
          <nav
            aria-label={t('app.name', 'Noor Admin')}
            className="min-h-0 flex-1 overflow-y-auto px-5 pt-[17px] pb-4"
          >
            {sections.map((section, index) => {
              return (
                <section
                  key={section.id}
                  aria-label={t(section.titleKey)}
                  className={cn(index > 0 && 'mt-[18px]')}
                >
                  <p className="mb-[14.5px] text-[13px] leading-4 font-medium text-[#707070] uppercase">
                    {t(section.titleKey)}
                  </p>
                  <div className="flex flex-col gap-[5px]">
                    {section.rows.map((row) => {
                      const label = t(row.labelKey);
                      if (isLinkRow(row)) {
                        return (
                          <SidebarLeafLink
                            key={row.id}
                            row={row}
                            label={label}
                            active={isPathActive(row.to)}
                          />
                        );
                      }
                      if (isGroupRow(row)) {
                        return (
                          <SidebarGroupRow
                            key={row.id}
                            row={row}
                            label={label}
                            open={isGroupOpen(row)}
                            onToggle={() => toggleGroup(row)}
                            tChild={(key) => t(key)}
                            isChildActive={isPathActive}
                          />
                        );
                      }
                      return <SidebarDisabledRow key={row.id} row={row} label={label} />;
                    })}
                  </div>
                </section>
              );
            })}
          </nav>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center">
          <div className="flex h-14 w-full shrink-0 items-center justify-center border-b border-[#374151]">
            <Link
              to="/merchants"
              aria-label="Noor Home"
              className="rounded outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <LogoMark className="size-8 text-[#FF6D00]" />
            </Link>
          </div>
          <button
            type="button"
            aria-label={t('nav.expandSidebar')}
            onClick={() => setExpanded(true)}
            className="mt-3 flex size-8 shrink-0 items-center justify-center rounded-[5px] text-[#d6dff0] transition-colors outline-none hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-white/40"
          >
            <RemixSidebarUnfoldIcon className="size-4" />
          </button>

          {/* Divider below the toggle */}
          <div aria-hidden="true" className="mt-3 h-px w-[50px] shrink-0 bg-[#374151]" />

          {/* Collapsed icon rail: one 26px tile per top-level row */}
          <nav
            aria-label={t('app.name', 'Noor Admin')}
            className="mt-3 flex min-h-0 flex-1 flex-col items-center gap-[5px] overflow-y-auto pb-4"
          >
            {flatRows.map((row) => {
              const label = t(row.labelKey);
              const active = isRowActive(row);

              if (isLinkRow(row)) {
                return (
                  <Tooltip key={row.id}>
                    <TooltipTrigger asChild>
                      <Link
                        to={row.to}
                        aria-label={label}
                        data-active={active}
                        className="rounded-[5px] outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                      >
                        <SidebarIconTile active={active} disabled={false}>
                          <row.icon className="size-5" />
                        </SidebarIconTile>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                );
              }

              if (isGroupRow(row) && !row.disabled) {
                return (
                  <Popover
                    key={row.id}
                    open={openRailGroup === row.id}
                    onOpenChange={(open) => setOpenRailGroup(open ? row.id : null)}
                  >
                    <Tooltip {...(openRailGroup === row.id ? { open: false } : {})}>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={label}
                            className="rounded-[5px] outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                          >
                            <SidebarIconTile active={active} disabled={false}>
                              <row.icon className="size-5" />
                            </SidebarIconTile>
                          </button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="right">{label}</TooltipContent>
                    </Tooltip>
                    <PopoverContent side="right" align="start" className="w-52 p-2">
                      <p className="px-2 py-1 text-sm font-semibold">{label}</p>
                      {row.children.map((child) =>
                        isChildLink(child) ? (
                          <Link
                            key={child.id}
                            to={child.to}
                            onClick={() => setOpenRailGroup(null)}
                            className="block rounded-md px-2 py-2 text-sm hover:bg-muted"
                          >
                            {t(child.labelKey)}
                          </Link>
                        ) : (
                          <span
                            key={child.id}
                            className="block px-2 py-2 text-sm opacity-50"
                          >
                            {t(child.labelKey)}
                          </span>
                        ),
                      )}
                    </PopoverContent>
                  </Popover>
                );
              }

              return (
                <SidebarIconTile key={row.id} label={label} active={false} disabled>
                  <row.icon className="size-5" />
                </SidebarIconTile>
              );
            })}
          </nav>
        </div>
      )}
    </aside>
  );
}

/** 26px icon tile shared by expanded rows and the collapsed rail. */
function SidebarIconTile({
  label,
  active,
  disabled,
  className,
  children,
}: {
  label?: string;
  active: boolean;
  disabled: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      title={disabled ? label : undefined}
      aria-label={disabled ? label : undefined}
      aria-disabled={disabled || undefined}
      className={cn(
        'flex size-[26px] shrink-0 items-center justify-center rounded-[5px] transition-colors',
        active ? 'bg-[#374151] text-white' : 'bg-[#1f2937] text-[#d6dff0]',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {children}
    </span>
  );
}

/** 6px down chevron shared by group headers (matches the Figma frame). */
function SidebarChevron() {
  return (
    <span
      aria-hidden="true"
      className="mr-3 ml-2 flex size-[8.5px] shrink-0 items-center justify-center"
    >
      <span className="size-[6px] rotate-45 border-r-2 border-b-2 border-white" />
    </span>
  );
}

/** Top-level leaf link: navigates directly, no chevron. */
function SidebarLeafLink({
  row,
  label,
  active,
}: {
  row: NavLinkRow;
  label: string;
  active: boolean;
}) {
  const RowIcon = row.icon;
  return (
    <Link
      to={row.to}
      data-active={active}
      className={cn(
        'relative flex h-[34px] w-full items-center rounded-[5px] pr-3 text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/40',
        active ? 'bg-[#1f2937]' : 'hover:bg-white/5',
      )}
    >
      <SidebarIconTile active={active} disabled={false} className="ml-1">
        <RowIcon className="size-5" />
      </SidebarIconTile>
      <SidebarLabel
        text={label}
        className={cn(
          'ml-2 min-w-0 flex-1 text-[14px] leading-[21px] font-medium',
          active ? 'text-white' : 'text-[#d6dff0]',
        )}
      />
    </Link>
  );
}

/** Disabled top-level row: visible per Figma, but not interactive. */
function SidebarDisabledRow({ row, label }: { row: NavDisabledRow; label: string }) {
  const RowIcon = row.icon;
  return (
    <span
      aria-disabled="true"
      className="relative flex h-[34px] w-full cursor-not-allowed items-center rounded-[5px] opacity-50"
    >
      <SidebarIconTile active={false} disabled={false} className="ml-1">
        <RowIcon className="size-5" />
      </SidebarIconTile>
      <span className="ml-2 min-w-0 flex-1 truncate text-[14px] leading-[21px] font-medium text-[#d6dff0]">
        {label}
      </span>
      {row.chevron && <SidebarChevron />}
    </span>
  );
}

/** Expandable group row (Мерчант, Бизнес, Настройки, ОПС) with dot sub-items. */
function SidebarGroupRow({
  row,
  label,
  open,
  onToggle,
  tChild,
  isChildActive,
}: {
  row: NavGroupRow;
  label: string;
  open: boolean;
  onToggle: () => void;
  tChild: (key: string) => string;
  isChildActive: (to: string) => boolean;
}) {
  const RowIcon = row.icon;
  const disabled = row.disabled === true;
  // Disabled groups render collapsed per Figma; interactive groups follow the
  // derived open state. The open group reads filled (#1f2937 row, #374151
  // tile, white label); selection never propagates upward beyond that.
  const expanded = disabled ? false : open;
  const highlighted = !disabled && expanded;

  const headerInner = (
    <>
      <SidebarIconTile active={highlighted} disabled={false} className="ml-1">
        <RowIcon className="size-5" />
      </SidebarIconTile>
      <SidebarLabel
        text={label}
        className={cn(
          'ml-2 min-w-0 flex-1 text-[14px] leading-[21px] font-medium',
          highlighted ? 'text-white' : 'text-[#d6dff0]',
        )}
      />
      <SidebarChevron />
    </>
  );

  return (
    <div className="flex flex-col">
      {disabled ? (
        <span
          aria-disabled="true"
          className="relative flex h-[34px] w-full cursor-not-allowed items-center rounded-[5px] opacity-50"
        >
          {headerInner}
        </span>
      ) : (
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className={cn(
            // text-left: <button> centers inline text per the UA stylesheet,
            // which the flex-1 label would otherwise inherit (other rows are
            // <span>/<a> and stay left-aligned).
            'relative flex h-[34px] w-full items-center rounded-[5px] text-left transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/40',
            highlighted ? 'bg-[#1f2937]' : 'hover:bg-white/5',
          )}
        >
          {headerInner}
        </button>
      )}
      {expanded && (
        <div className="flex flex-col">
          {row.children.map((child, index) => {
            const childLabel = tChild(child.labelKey);
            const divider = (
              <span
                aria-hidden="true"
                className={cn(
                  'absolute top-[3px] left-[22.5px] h-2 w-[0.5px] bg-[#374151]',
                  index > 0 && 'top-[-2px]',
                )}
              />
            );
            if (isChildLink(child)) {
              // A nested destination wins: only the most specific child
              // reads active when a parent and child both match.
              const childActive =
                isChildActive(child.to) &&
                !row.children.some(
                  (other) =>
                    isChildLink(other) &&
                    other.to.startsWith(`${child.to}/`) &&
                    isChildActive(other.to),
                );
              return (
                <Link
                  key={child.id}
                  to={child.to}
                  data-active={childActive}
                  className="relative flex h-[35.5px] w-full items-center rounded-[5px] transition-colors outline-none hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-white/40"
                >
                  {divider}
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute top-1/2 left-5 size-2 -translate-y-1/2 rounded-full',
                      childActive ? 'bg-white' : 'bg-[#374151]',
                    )}
                  />
                  <SidebarLabel
                    text={childLabel}
                    className={cn(
                      'ml-9 min-w-0 flex-1 text-[13px] leading-[19.5px] font-normal',
                      // The active leaf reads white like its open header —
                      // color (and the dot) carry the state, never weight.
                      childActive ? 'text-white' : 'text-[#9ca3af]',
                    )}
                  />
                </Link>
              );
            }
            return (
              <span
                key={child.id}
                aria-disabled={true}
                className="relative flex h-[35.5px] w-full cursor-not-allowed items-center opacity-50"
              >
                {divider}
                <span
                  aria-hidden="true"
                  className="absolute top-1/2 left-5 size-2 -translate-y-1/2 rounded-full bg-[#374151]"
                />
                <SidebarLabel
                  text={childLabel}
                  className="ml-9 min-w-0 flex-1 text-[13px] leading-[19.5px] font-normal text-[#9ca3af]"
                />
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Expanded navigation already exposes its labels; no hover tooltip is needed. */
function SidebarLabel({ text, className }: { text: string; className?: string }) {
  return <span className={cn('min-w-0 truncate', className)}>{text}</span>;
}
